/**
 * Per-area progress stacked-bar chart. Same layout / semantics as the
 * assignee workload chart, but keyed on 領域 (`TaskDoc.areaIds`, which is
 * multi-value — `computeAreaProgress` expands a task into each of its
 * areas, so a task can appear in several rows). Reuses the shared area
 * color palette via inline class lookups — see `areaColor.ts` for the
 * resolution rules.
 */
import type { AreaProgressRow } from '../../lib/dashboard';
import { SIGNAL_COLOR } from '../DagView/signal';

export interface AreaProgressChartProps {
  rows: readonly AreaProgressRow[];
}

export function AreaProgressChart({ rows }: AreaProgressChartProps): JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-4 text-sm text-slate-500">
        領域別のタスクはまだありません
      </div>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {rows.map((row) => {
        const total = Math.max(row.total, 1);
        const segments: Array<{ key: string; count: number; color: string }> = [
          { key: 'done', count: row.done, color: SIGNAL_COLOR.done },
          { key: 'in_progress', count: row.inProgress, color: SIGNAL_COLOR.in_progress },
          { key: 'blocked', count: row.blocked, color: SIGNAL_COLOR.blocked },
          { key: 'not_started', count: row.notStarted, color: SIGNAL_COLOR.not_started },
        ];
        return (
          <li
            key={row.areaId ?? '__unassigned__'}
            className="flex items-center gap-3 text-sm"
          >
            <div className="w-28 shrink-0 truncate text-slate-700" title={row.label}>
              {row.label}
            </div>
            <div className="flex h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
              {segments.map((seg) =>
                seg.count > 0 ? (
                  <div
                    key={seg.key}
                    className="h-full"
                    style={{
                      width: `${(seg.count / total) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.key}: ${seg.count}`}
                  />
                ) : null,
              )}
            </div>
            <div className="w-24 shrink-0 text-right text-[11px] tabular-nums text-slate-600">
              {row.done}/{row.total} ({row.donePercent}%)
            </div>
          </li>
        );
      })}
    </ul>
  );
}
